import { useEffect, useLayoutEffect, useRef } from 'react';
import { ARMED_ATTR, IN_ATTR, initReveals, replayReveal } from '@aquilixshop/ui-react';

/**
 * THE ENTRANCE ANIMATIONS, IN THE EDITOR (founder, 2026-09-18: "les animations doivent être aussi dans
 * le builder").
 *
 * Until this existed the canvas armed nothing and observed nothing, so the whole "Animations" group —
 * every section's entrance, and the "One item at a time" cascade — was invisible while editing. The
 * seller set a movement she could not see and had to publish to find out what she had chosen. The
 * clearest sign it was an oversight rather than a decision: `replayReveal` was written FOR the canvas,
 * documented as "the canvas calls this when the setting changes", and never called by anything.
 *
 * ── WHY THE EDITOR CANNOT JUST DO WHAT THE STOREFRONT DOES ───────────────────────────────────────────
 * A reveal's resting state is INVISIBLE. On a shop that is fine: the reader scrolls, the band arrives.
 * In an editor the same rule can hand the seller a section she cannot see, and a section she cannot see
 * is a section she cannot edit. So the canvas keeps the storefront's engine and adds two guarantees the
 * storefront does not need:
 *
 *   1. **Changing an animation setting replays that section's entrance on the spot.** She is looking at
 *      the section she is editing; she must not have to scroll away and back to judge her own choice.
 *   2. **Selecting a section forces it to have arrived.** Whatever the observer did or did not see —
 *      a section opened from the left rail, one inside a collapsed area, one that never crossed the
 *      trigger line — the moment it is the section being worked on, it is visible. Full stop.
 *
 * The arming flag lives on `<html>`, exactly as on the storefront, because every hiding rule in
 * theme.css is scoped to it. Nothing else in the builder chrome carries an animation marker, so the flag
 * reaches the canvas and only the canvas. It is reference-counted: two surfaces (edit canvas and clean
 * preview) can be mounted across a transition, and the first to unmount must not disarm the other.
 */

let armCount = 0;

function arm(): void {
  armCount += 1;
  if (armCount === 1) document.documentElement.setAttribute(ARMED_ATTR, '');
}

function disarm(): void {
  armCount = Math.max(0, armCount - 1);
  if (armCount === 0) document.documentElement.removeAttribute(ARMED_ATTR);
}

/** The animated element a section renders — the one theme.css hides and the engine marks arrived. */
function revealTargetOf(root: ParentNode, sectionId: string): HTMLElement | null {
  const host = root.querySelector<HTMLElement>(`[data-section-id="${CSS.escape(sectionId)}"]`);
  if (!host) return null;
  if (host.matches('[data-aqx-anim], [style*="--aqx-anim-name"]')) return host;
  return host.querySelector<HTMLElement>('[data-aqx-anim], [style*="--aqx-anim-name"]');
}

export interface CanvasRevealsInput {
  /**
   * The canvas root the engine binds inside — the NODE, not a `RefObject`.
   *
   * A ref object is stable, so an effect keyed on it never re-runs, and toggling the clean preview does
   * NOT unmount `BuilderCanvas`: the edit `<div data-aqx-page>` merely goes away and comes back as a
   * DIFFERENT node. Keyed on the ref, the layout effect below neither re-bound nor cleaned up — the
   * observer stayed on the detached div while `data-aqx-anim-armed` stayed on `<html>`, which is
   * `opacity: 0` with nobody left to mark anything arrived. Every animated section came back INVISIBLE
   * after a round trip through the preview (founder, 2026-09-19). The node makes the remount an ordinary
   * dependency.
   */
  root: HTMLElement | null;
  /**
   * One line per section, `id:animation:pace:stagger`.
   *
   * It drives the REPLAY only. Binding keys off `ids` instead, and the two are deliberately separate:
   * re-binding on a settings change re-observed the section a frame after `replayReveal` had taken its
   * arrival mark off, the observer put it straight back, and the entrance "played" without the browser
   * ever painting a hidden frame. Measured on 2026-09-18: 186 frames sampled, not one below opacity 1.
   */
  signature: string;
  /** The section ids, in order — the only thing that decides when new nodes need observing. */
  ids: string;
  /** The section being edited, if any — it is force-shown, never left waiting on the observer. */
  selectedId: string | null;
}

export function useCanvasReveals({ root, signature, ids, selectedId }: CanvasRevealsInput): void {
  // ── Arm + observe, on the section LIST only. ─────────────────────────────────────────────────────
  //
  // `useLayoutEffect`, not `useEffect`: the arming flag has to be on `<html>` in the SAME commit that
  // puts the sections in the DOM, or the browser paints them once at full strength before the hiding
  // rules apply — and an entrance whose first frame is the finished state is not an entrance. The
  // storefront gets this for free from the inline <head> snippet; the canvas has to ask for it.
  useLayoutEffect(() => {
    if (!root) return;
    arm();
    const off = initReveals(root);
    return () => {
      off();
      disarm();
    };
  }, [root, ids]);

  // ── Replay the section whose animation the seller just changed. ──────────────────────────────────
  const previous = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    if (!root) return;
    const now = new Map<string, string>();
    for (const line of signature.split('\n')) {
      if (!line) continue;
      const id = line.slice(0, line.indexOf(':'));
      if (id) now.set(id, line);
    }
    const before = previous.current;
    previous.current = now;
    // First pass: the sections are arriving for the first time anyway — replaying would double the
    // movement on load, which reads as a stutter.
    if (!before) return;
    for (const [id, line] of now) {
      const was = before.get(id);
      if (was === undefined || was === line) continue;
      const el = revealTargetOf(root, id);
      if (el) replayReveal(el);
    }
  }, [root, signature]);

  // ── The section being edited is ALWAYS visible. ──────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedId || !root) return;
    // A frame late on purpose: selecting from the rail scrolls the canvas, and the observer may be about
    // to reveal this very element. Waiting lets it do its own job and keeps this a failsafe, not a race.
    const t = window.setTimeout(() => {
      const el = revealTargetOf(root, selectedId);
      // `replayReveal`, not a bare `setAttribute`: it is the only path that also NUMBERS the staggered
      // items (`--aqx-anim-i`). Marking the section arrived by hand would show it, but with every item
      // at delay 0 — the seller would be looking at a cascade she configured and cannot see.
      if (el && !el.hasAttribute(IN_ATTR)) replayReveal(el);
    }, 120);
    return () => window.clearTimeout(t);
  }, [root, selectedId]);
}
